import { CostCenter } from '@Types/api/common/dictionary';
import {
  MaterialRequisitionDocumentResponse,
  MaterialRequisitionItemFormResponse,
  PurchaseDocumentResponse,
  PurchaseItemResponse,
} from '@Types/api/form';
import { TenderGetDocumentResponse } from '@Types/api/form/tenderRequest/TenderGetDocumentResponse';
import { TenderMaterial } from '@Types/api/form/tenderRequest/TenderMaterialResponse';
import { TenderSearchVendorsResponse } from '@Types/api/form/tenderRequest/TenderSearchVendorsResponse';
import { TenderVendorListResponse } from '@Types/api/form/tenderRequest/TenderVendorListResponse';
import { CompanyDict, TypeDict } from '@Types/index';

export const findCompanyType = (
  companyId: number,
  companies: CompanyDict[]
): CompanyDict | undefined =>
  companies.find((item) => item.compId === companyId);

export const findCostCenter = (
  costCenters: Array<CostCenter>,
  costCenterId: number
): CostCenter | undefined =>
  costCenters.find((costCenter) => costCenter.id === costCenterId);

export const findCostCenterType = (
  costCenterTypes: Array<TypeDict>,
  costCenterTypeId: number
): TypeDict | undefined =>
  costCenterTypes.find(
    (costCenterType) => costCenterType.codeNum === costCenterTypeId
  );

export const findMrForType = (
  mrForTypes: Array<TypeDict>,
  mrForId: number
): TypeDict | undefined =>
  mrForTypes.find((mrForType) => mrForType.codeNum === mrForId);

export const findMaterialItemBySearchValue = (
  listItems: Array<MaterialRequisitionItemFormResponse>,
  searchValue: string
): Array<MaterialRequisitionItemFormResponse> => {
  return listItems.filter((item) => {
    if (item.actCode && item.resCode && item.matCode) {
      return (
        item.actCode.includes(searchValue) ||
        item.resCode.includes(searchValue) ||
        item.matCode.includes(searchValue)
      );
    }

    if (item.plantCode && item.ledgerCode && item.matCode) {
      return (
        item.plantCode.includes(searchValue) ||
        String(item.ledgerCode).includes(searchValue) ||
        item.matCode.includes(searchValue)
      );
    }

    if (item.ledgerCode && item.matCode) {
      return (
        item.ledgerCode.includes(searchValue) ||
        item.matCode.includes(searchValue)
      );
    }

    return item;
  });
};

export const findPurchaseMaterialItemBySearchValue = (
  listItems: Array<PurchaseItemResponse>,
  searchValue: string
): Array<PurchaseItemResponse> => {
  return listItems.filter((item) => {
    if (item.matCode && item.matDesc) {
      return (
        item.matDesc.includes(searchValue) || item.matCode.includes(searchValue)
      );
    }

    return item;
  });
};

export const findPurchaseDocumentItemBySearchValue = (
  listDocuments: Array<PurchaseDocumentResponse>,
  searchValue: string
): Array<PurchaseDocumentResponse> => {
  return listDocuments.filter((document) => {
    const docName = document.documentName.toLocaleLowerCase();
    const criteriaSearch = searchValue.toLocaleLowerCase();
    return docName.includes(criteriaSearch);
  });
};

export const findDocumentItemBySearchValue = (
  listDocuments: Array<MaterialRequisitionDocumentResponse>,
  searchValue: string
): Array<MaterialRequisitionDocumentResponse> => {
  return listDocuments.filter((document) => {
    const docName = document.docName.toLocaleLowerCase();
    const criteriaSearch = searchValue.toLocaleLowerCase();
    return docName.includes(criteriaSearch);
  });
};

export const findTenderMaterialItemBySearchValue = (
  listItems: Array<TenderMaterial>,
  searchValue: string
): Array<TenderMaterial> => {
  return listItems.filter((item) => {
    if (item.materialCode && item.materialDesc) {
      return (
        item.materialCode.includes(searchValue) ||
        item.materialDesc
          .toLowerCase()
          .includes(searchValue.trim().toLowerCase())
      );
    }
    return false;
  });
};

export const findTenderVendorsBySearchValue = (
  listItems: TenderVendorListResponse,
  searchValue: string
): TenderVendorListResponse => {
  return listItems.filter((item) => {
    if (item.name && item.vendorId) {
      return (
        String(item.vendorId).includes(searchValue) ||
        item.name.toLowerCase().includes(searchValue.trim().toLowerCase())
      );
    }
    return false;
  });
};

export const findTenderDocumentItemBySearchValue = (
  listDocuments: TenderGetDocumentResponse[],
  searchValue: string
): TenderGetDocumentResponse[] => {
  return listDocuments.filter((document) => {
    return document.documentName
      .toLowerCase()
      .includes(searchValue.trim().toLowerCase());
  });
};

export const findTenderInitiationVendorsBySearchValue = (
  listItems: TenderSearchVendorsResponse[],
  searchValue: string
): TenderSearchVendorsResponse[] => {
  return listItems.filter((item) => {
    if (item.vendorName && item.vendorId) {
      return (
        String(item.vendorId).includes(searchValue) ||
        item.vendorName.toLowerCase().includes(searchValue.trim().toLowerCase())
      );
    }
    return false;
  });
};
